import React from "react";
import { TaskCreatorButton } from "../TaskCreatorButton";
import { serializeScaleState } from "../../lib/taskEngine";
import type { NoteName } from "../../lib/guitarEngine";

const PATTERNS = [
  { value: "linear", label: "Lineal" },
  { value: "thirds", label: "Saltos 3ra" },
  { value: "fourths", label: "Saltos 4ta" },
  { value: "fifths", label: "Saltos 5ta" },
];

const ROUTES = [
  { mode: "off" as const, label: "Sin ruta" },
  { mode: "tonic" as const, label: "Tónica" },
  { mode: "transition" as const, label: "Transición" },
  { mode: "recorrido" as const, label: "Recorrido" },
];

export interface ScalePlaybackControlsProps {
  rootNote: NoteName;
  scaleType: string;
  scaleLabel: string;
  routeMode: "off" | "tonic" | "transition" | "recorrido";
  setRouteMode: (m: "off" | "tonic" | "transition" | "recorrido") => void;
  octaves: number;
  setOctaves: (n: number) => void;
  direction: "asc" | "desc" | "both";
  setDirection: (d: "asc" | "desc" | "both") => void;
  pattern: string;
  bpm: number;
  setBpm: (n: number) => void;
  routeLocked?: boolean;
  variant?: "default" | "serenity";
}

export const ScalePlaybackControls: React.FC<ScalePlaybackControlsProps> = ({
  rootNote,
  scaleType,
  scaleLabel,
  routeMode,
  setRouteMode,
  octaves,
  setOctaves,
  direction,
  setDirection,
  pattern,
  bpm,
  setBpm,
  routeLocked = false,
  variant = "default",
}) => {
  if (variant === "serenity") {
    return (
      <div className="space-y-7 py-1">
        <div>
          <p className="text-[10px] font-sans uppercase tracking-widest text-white/40 mb-3">Ruta</p>
          <div className="flex flex-wrap gap-x-4 gap-y-1">
            {ROUTES.map((r) => (
              <button
                key={r.mode}
                type="button"
                disabled={routeLocked}
                onClick={() => !routeLocked && setRouteMode(r.mode)}
                className={`text-sm font-sans transition-colors ${
                  routeLocked ? "opacity-30 cursor-not-allowed" : ""
                } ${
                  routeMode === r.mode
                    ? "text-white"
                    : "text-white/40 hover:text-white/70"
                }`}
              >
                {routeMode === r.mode ? `[${r.label}]` : r.label}
              </button>
            ))}
          </div>
        </div>

        <div>
          <p className="text-[10px] font-sans uppercase tracking-widest text-white/40 mb-3">Octavas</p>
          <div className="flex gap-4 font-sans text-sm">
            {[1, 2, 3].map((o) => (
              <button
                key={o}
                type="button"
                onClick={() => setOctaves(o)}
                className={`transition-colors ${
                  octaves === o ? "text-emerald-300" : "text-white/40 hover:text-white/70"
                }`}
              >
                {octaves === o ? `[${o}]` : o}
              </button>
            ))}
          </div>
        </div>

        <div>
          <p className="text-[10px] font-sans uppercase tracking-widest text-white/40 mb-3">Dirección</p>
          <div className="flex gap-4 font-sans text-sm">
            {(
              [
                { d: "asc" as const, label: "↑" },
                { d: "desc" as const, label: "↓" },
                { d: "both" as const, label: "↕" },
              ] as const
            ).map(({ d, label }) => (
              <button
                key={d}
                type="button"
                onClick={() => setDirection(d)}
                className={`transition-colors ${
                  direction === d ? "text-purple-300" : "text-white/40 hover:text-white/70"
                }`}
              >
                {direction === d ? `[${label}]` : label}
              </button>
            ))}
          </div>
        </div>

        <div>
          <div className="flex items-baseline justify-between mb-2">
            <p className="text-[10px] font-sans uppercase tracking-widest text-white/40">Tempo</p>
            <span className="text-[11px] font-sans text-white/50 tabular-nums">{bpm}</span>
          </div>
          <input
            type="range"
            min={40}
            max={200}
            value={bpm}
            onChange={(e) => setBpm(Number(e.target.value))}
            className="explore-tempo-slider w-full appearance-none bg-transparent cursor-pointer"
            style={{ height: "1px" }}
          />
        </div>

        <TaskCreatorButton
          type="scale"
          variant="link"
          title={`Escala ${rootNote} ${scaleLabel}`}
          description={`Patrón: ${pattern === "linear" && direction === "both" ? "Ida y vuelta" : PATTERNS.find((p) => p.value === pattern)?.label}, ${octaves} octava(s)`}
          config={serializeScaleState(rootNote, scaleType, octaves, direction, pattern)}
        />
      </div>
    );
  }

  return (
    <div className="space-y-3 pt-3 border-t border-white/15">
      <div className="text-[10px] font-sans font-semibold uppercase tracking-wider text-slate-400">
        Recorrido
      </div>

      <div>
        <div className="text-[10px] font-sans text-slate-500 mb-1.5">Ruta visual</div>
        <div className="flex flex-wrap gap-1.5">
          {(
            [
              { mode: "off" as const, label: "✕", title: "Sin ruta" },
              { mode: "tonic" as const, label: "🎯", title: "Ruta de la tónica" },
              { mode: "transition" as const, label: "🔗", title: "Transición entre cajas" },
              { mode: "recorrido" as const, label: "🐍", title: "Recorrido de notas" },
            ] as const
          ).map((r) => (
            <button
              key={r.mode}
              type="button"
              disabled={routeLocked}
              onClick={() => !routeLocked && setRouteMode(r.mode)}
              className={`w-9 h-9 rounded-lg flex items-center justify-center text-xs transition-all ${
                routeLocked ? "opacity-40 cursor-not-allowed" : ""
              } ${
                routeMode === r.mode
                  ? "bg-[var(--bordona-accent)] text-white shadow-sm"
                  : "bg-white/[0.07] text-slate-300 hover:bg-white/10"
              }`}
              title={r.title}
            >
              {r.label}
            </button>
          ))}
        </div>
      </div>

      <div>
        <div className="text-[10px] font-sans text-slate-500 mb-1.5">Octavas</div>
        <div className="flex gap-1.5">
          {[1, 2, 3].map((o) => (
            <button
              key={o}
              type="button"
              onClick={() => setOctaves(o)}
              className={`flex-1 h-9 rounded-lg text-xs font-sans font-bold transition-all ${
                octaves === o
                  ? "bg-[var(--bordona-accent)] text-white"
                  : "bg-white/[0.07] text-slate-300 hover:bg-white/10"
              }`}
            >
              {o}
            </button>
          ))}
        </div>
      </div>

      <div>
        <div className="text-[10px] font-sans text-slate-500 mb-1.5">Dirección</div>
        <div className="flex gap-1.5">
          {(["asc", "desc", "both"] as const).map((d) => (
            <button
              key={d}
              type="button"
              onClick={() => setDirection(d)}
              className={`flex-1 h-9 rounded-lg flex items-center justify-center transition-all ${
                direction === d
                  ? "bg-[#2C2825] text-white"
                  : "bg-white/[0.07] text-slate-300 hover:bg-white/10"
              }`}
              title={d === "asc" ? "Ascendente" : d === "desc" ? "Descendente" : "Ida y vuelta"}
            >
              {d === "asc" ? "↑" : d === "desc" ? "↓" : "↕"}
            </button>
          ))}
        </div>
      </div>

      <div>
        <div className="flex items-center justify-between text-[10px] font-sans text-slate-500 mb-1">
          <span>Tempo</span>
          <span className="font-bold text-slate-200 tabular-nums">{bpm} BPM</span>
        </div>
        <input
          type="range"
          min={40}
          max={200}
          value={bpm}
          onChange={(e) => setBpm(Number(e.target.value))}
          className="w-full h-1.5 accent-[var(--bordona-accent)]"
        />
      </div>

      <TaskCreatorButton
        type="scale"
        title={`Escala ${rootNote} ${scaleLabel}`}
        description={`Patrón: ${pattern === "linear" && direction === "both" ? "Ida y vuelta" : PATTERNS.find((p) => p.value === pattern)?.label}, ${octaves} octava(s)`}
        config={serializeScaleState(rootNote, scaleType, octaves, direction, pattern)}
      />
    </div>
  );
};
